<script>
import Portal from "../../layouts/portal";
import { cargarEventos, enlaceWhatsapp } from "@/services/portal";

/**
 * "Qué plan hoy": los eventos que publican los comercios (colección
 * `queplanhoy`). No lleva muro de pago: en la app también son visibles para
 * cualquier miembro con cuenta.
 */
export default {
  page: { title: "Qué plan hoy" },
  components: { Portal },
  data() {
    return {
      eventos: [],
      busqueda: "",
      cargando: true,
      error: null,
      seleccionado: null,
      mostrarDetalle: false,
    };
  },
  computed: {
    eventosFiltrados() {
      const q = this.busqueda.trim().toLowerCase();
      if (!q) return this.eventos;
      return this.eventos.filter((e) =>
        [e.nombre, e.comercio, e.descripcion, e.ubicacion]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  methods: {
    cuando(evento) {
      const inicio = evento.fechaInicio || "";
      const fin = evento.fechaFin && evento.fechaFin !== evento.fechaInicio ? ` al ${evento.fechaFin}` : "";
      const hora = evento.horaInicio ? ` · ${evento.horaInicio}` : "";
      return `${inicio}${fin}${hora}`.trim() || "Fecha por confirmar";
    },
    whatsapp(evento) {
      return enlaceWhatsapp(evento.contacto, `Hola, vi "${evento.nombre}" en Recnet.`);
    },
    /**
     * Enlace a Google Maps para llegar al evento. Los eventos no guardan
     * coordenadas, sólo la ubicación como texto, así que se abre una búsqueda
     * por dirección: Maps la resuelve y ofrece cómo llegar.
     */
    mapa(evento) {
      const lugar = evento && evento.ubicacion;
      if (!lugar) return null;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lugar)}`;
    },
    verDetalle(evento) {
      this.seleccionado = evento;
      this.mostrarDetalle = true;
    },
    async cargar() {
      this.cargando = true;
      this.error = null;
      try {
        this.eventos = await cargarEventos();
      } catch (err) {
        console.error("[portal] error al cargar eventos:", err);
        this.error = "No pudimos cargar los eventos. Inténtalo de nuevo en un momento.";
      } finally {
        this.cargando = false;
      }
    },
  },
  mounted() {
    this.cargar();
  },
};
</script>

<template>
  <Portal>
    <h1 class="h3 mb-1">Qué plan hoy</h1>
    <p class="text-muted">Lo que está pasando en la red. Sólo aparecen los eventos vigentes.</p>

    <b-form-input v-model="busqueda" type="search" placeholder="Buscar evento o comercio…"
      class="portal-buscador mb-4" />

    <b-alert :show="!!error" variant="warning">{{ error }}</b-alert>
    <p v-if="cargando" class="text-muted">Cargando eventos…</p>
    <p v-else-if="eventosFiltrados.length === 0" class="text-muted">
      Por ahora no hay eventos programados.
    </p>

    <b-row v-else>
      <b-col v-for="evento in eventosFiltrados" :key="evento.id" md="6" xl="4" class="mb-4">
        <div class="evento-card" @click="verDetalle(evento)">
          <div class="evento-portada">
            <img v-if="evento.imagenUrl" :src="evento.imagenUrl" :alt="evento.nombre" />
            <div v-else class="evento-portada-vacia"><i class="mdi mdi-calendar-star"></i></div>
          </div>
          <div class="evento-cuerpo">
            <h3 class="evento-nombre">{{ evento.nombre }}</h3>
            <p class="evento-comercio mb-1">{{ evento.comercio }}</p>
            <p class="evento-cuando mb-0">
              <i class="mdi mdi-calendar-clock"></i> {{ cuando(evento) }}
            </p>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-modal v-model="mostrarDetalle" size="lg" centered hide-footer
      :title="seleccionado ? seleccionado.nombre : ''">
      <div v-if="seleccionado">
        <img v-if="seleccionado.imagenUrl" :src="seleccionado.imagenUrl" :alt="seleccionado.nombre"
          class="evento-detalle-img mb-3" />

        <ul class="list-unstyled mb-3">
          <li v-if="seleccionado.comercio" class="mb-2"><strong>Organiza:</strong> {{ seleccionado.comercio }}</li>
          <li class="mb-2"><strong>Cuándo:</strong> {{ cuando(seleccionado) }}</li>
          <li v-if="seleccionado.horaFin" class="mb-2"><strong>Termina:</strong> {{ seleccionado.horaFin }}</li>
          <li v-if="seleccionado.ubicacion" class="mb-2"><strong>Dónde:</strong> {{ seleccionado.ubicacion }}</li>
        </ul>

        <p v-if="seleccionado.descripcion" class="evento-descripcion">{{ seleccionado.descripcion }}</p>

        <div class="d-flex flex-wrap mt-2">
          <a v-if="mapa(seleccionado)" :href="mapa(seleccionado)" target="_blank" rel="noopener noreferrer"
            class="btn btn-primary mr-2 mb-2">
            <i class="mdi mdi-map-marker"></i> Ir al evento
          </a>
          <a v-if="whatsapp(seleccionado)" :href="whatsapp(seleccionado)" target="_blank" rel="noopener noreferrer"
            class="btn btn-success mb-2">
            <i class="mdi mdi-whatsapp"></i> Preguntar por WhatsApp
          </a>
        </div>
      </div>
    </b-modal>
  </Portal>
</template>

<style scoped>
.portal-buscador {
  max-width: 360px;
}

.evento-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  height: 100%;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.evento-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

/* Misma caja para todas las portadas; ver la nota en `sesiones.vue` sobre por
   qué se usa `padding-top` y no `aspect-ratio`. Aquí las imágenes son fotos
   subidas por los comercios, así que se mantiene el formato apaisado 16:9. */
.evento-portada {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background: #ede9f5;
  overflow: hidden;
}

.evento-portada img,
.evento-portada-vacia {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.evento-portada img {
  object-fit: cover;
  object-position: center;
}

.evento-portada-vacia {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 44px;
  color: #4b2e83;
}

.evento-cuerpo {
  padding: 16px;
  flex: 1;
}

.evento-nombre {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.evento-comercio {
  color: #4b2e83;
  font-size: 0.9rem;
}

.evento-cuando {
  color: #74788d;
  font-size: 0.8rem;
}

.evento-detalle-img {
  width: 100%;
  max-height: 280px;
  object-fit: cover;
  border-radius: 8px;
}

.evento-descripcion {
  white-space: pre-wrap;
  line-height: 1.6;
}
</style>
